import React, { useEffect, useMemo, useState } from 'react'
import {
  ArrowLeft,
  Timer,
  Trophy,
  RefreshCw,
  Users,
  Check,
  Play,
  Sparkles,
  Plus,
  Minus,
} from 'lucide-react'
import { CATEGORIES } from './themes.js'
import { DIFFICULTIES, generateCase } from './engine.js'
import { awardBp } from '../lib/store.js'

const newSeed = () => Math.floor(Math.random() * 1e9)

function CategoryPicker({ value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
      {CATEGORIES.map((c) => {
        const active = value === c.id
        return (
          <button
            key={c.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(c.id)}
            className={`text-left p-3 rounded-2xl border transition-all cursor-pointer disabled:opacity-50 ${
              active
                ? 'border-cyan-500/60 bg-cyan-950/50 shadow-lg shadow-cyan-500/10'
                : 'border-slate-800 bg-[#0e131f] hover:border-slate-700'
            }`}
          >
            <div className="text-xl">{c.emoji}</div>
            <div className="text-xs font-bold text-white mt-1">{c.label}</div>
            <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">{c.blurb}</div>
          </button>
        )
      })}
    </div>
  )
}

function SuspectCard({ suspect, index, selected, onSelect, revealed, isCulprit, isLiar, disabled }) {
  const border = revealed
    ? isCulprit
      ? 'border-emerald-500/70 bg-emerald-950/40'
      : 'border-slate-800 bg-[#0e131f] opacity-70'
    : selected
      ? 'border-cyan-500 bg-cyan-950/30 shadow-lg shadow-cyan-500/10'
      : 'border-slate-800 bg-[#0e131f] hover:border-slate-700'

  return (
    <button
      type="button"
      disabled={disabled || revealed}
      onClick={() => onSelect?.(suspect.id)}
      className={`w-full text-left p-3.5 rounded-2xl border transition-all cursor-pointer disabled:cursor-default ${border}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-black text-slate-300">
            {index + 1}
          </span>
          <span className="text-sm font-bold text-white">{suspect.name}</span>
          {revealed && isCulprit && (
            <span className="px-1.5 py-0.5 rounded-md bg-emerald-900/70 border border-emerald-500/40 text-emerald-300 text-[9px] font-black uppercase">
              Culpable
            </span>
          )}
          {revealed && isLiar && !isCulprit && (
            <span className="px-1.5 py-0.5 rounded-md bg-rose-950/70 border border-rose-500/40 text-rose-300 text-[9px] font-black uppercase">
              Miente
            </span>
          )}
        </div>
        {revealed && (isCulprit ? <Check size={16} className="text-emerald-400" /> : null)}
      </div>
      <div className="mt-2.5 space-y-1.5">
        {suspect.lines.map((line, i) => (
          <p key={i} className="text-[12px] text-slate-300 leading-snug pl-9 relative">
            <span className="absolute left-3 top-0 text-slate-600">”</span>
            {line}
          </p>
        ))}
      </div>
    </button>
  )
}

function CaseBoard({ puzzle, onSelect, selected, revealed, onReveal, onNext, onExit, seconds = null, party = false }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onExit}
          className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
        >
          <ArrowLeft size={16} /> Salir
        </button>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 rounded-lg bg-slate-900 border border-slate-800 text-[10px] font-bold text-slate-300">
            {puzzle.category.emoji} {puzzle.category.label}
          </span>
          <span className="px-2 py-1 rounded-lg bg-slate-900 border border-slate-800 text-[10px] font-bold text-cyan-300">
            {puzzle.difficulty.label}
          </span>
          {seconds != null && !revealed && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-950/70 border border-amber-500/40 text-amber-300 text-[11px] font-black">
              <Timer size={13} /> {seconds}s
            </span>
          )}
        </div>
      </div>

      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#101828] to-[#0b1220] border border-slate-800">
        <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-cyan-400">
          <Sparkles size={14} /> El caso
        </div>
        <p className="text-sm text-slate-200 mt-2 leading-relaxed">{puzzle.intro}</p>
      </div>

      <div className="space-y-2.5">
        {puzzle.suspects.map((s, i) => (
          <SuspectCard
            key={s.id}
            suspect={s}
            index={i}
            selected={selected === s.id}
            onSelect={onSelect}
            revealed={revealed}
            isCulprit={s.id === puzzle.solutionId}
            isLiar={puzzle.liarIds.includes(s.id)}
          />
        ))}
      </div>

      {!revealed ? (
        <div className="sticky bottom-2">
          <button
            type="button"
            disabled={!party && !selected}
            onClick={onReveal}
            className={`w-full py-3 rounded-xl font-black text-sm shadow-lg active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer ${
              party
                ? 'bg-amber-500 text-slate-950 shadow-amber-500/25'
                : 'bg-cyan-500 text-slate-950 shadow-cyan-500/25'
            }`}
          >
            {party ? 'Revelar la verdad' : selected ? 'Confirmar culpable' : 'Elige un sospechoso'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="p-4 rounded-2xl bg-emerald-950/40 border border-emerald-500/40">
            <div className="text-[11px] font-black uppercase tracking-wider text-emerald-400">
              La verdad
            </div>
            <p className="text-sm text-emerald-100 mt-1.5 leading-relaxed">{puzzle.explanation}</p>
          </div>
          <button
            type="button"
            onClick={onNext}
            className="w-full py-3 rounded-xl bg-cyan-500 text-slate-950 font-black text-sm shadow-lg shadow-cyan-500/25 active:scale-[0.99] cursor-pointer flex items-center justify-center gap-2"
          >
            <RefreshCw size={16} /> Siguiente caso
          </button>
        </div>
      )}
    </div>
  )
}

export function DetectiveGame({ onBack, onComplete }) {
  const [mode, setMode] = useState(null) // null | 'solo' | 'copas'
  const [categoryId, setCategoryId] = useState('logica')
  const [difficultyId, setDifficultyId] = useState(2)

  // solo state
  const [seed, setSeed] = useState(newSeed)
  const [selected, setSelected] = useState(null)
  const [revealed, setRevealed] = useState(false)
  const [solved, setSolved] = useState(0)

  // party state
  const [players, setPlayers] = useState([
    { id: 'p0', name: 'Jugador 1', score: 0 },
    { id: 'p1', name: 'Jugador 2', score: 0 },
  ])
  const [round, setRound] = useState(1)
  const [rounds, setRounds] = useState(5)
  const [marked, setMarked] = useState([])
  const [seconds, setSeconds] = useState(60)
  const [partyOver, setPartyOver] = useState(false)

  const puzzle = useMemo(
    () => generateCase({ seed, categoryId, difficultyId: mode === 'copas' ? 2 : difficultyId }),
    [seed, categoryId, difficultyId, mode],
  )

  // countdown for party mode
  useEffect(() => {
    if (mode !== 'copas' || revealed || partyOver) return
    if (seconds <= 0) {
      setRevealed(true)
      return
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [mode, seconds, revealed, partyOver])

  const setCategory = (id) => {
    setCategoryId(id)
  }

  const startSolo = () => {
    setMode('solo')
    setSeed(newSeed())
    setSelected(null)
    setRevealed(false)
    setSolved(0)
  }

  const startParty = () => {
    setMode('copas')
    setSeed(newSeed())
    setSelected(null)
    setRevealed(false)
    setRound(1)
    setMarked([])
    setSeconds(60)
    setPartyOver(false)
    setPlayers((ps) => ps.map((p) => ({ ...p, score: 0 })))
  }

  const nextSoloCase = () => {
    setSeed(newSeed())
    setSelected(null)
    setRevealed(false)
  }

  const confirmSolo = () => {
    if (!selected) return
    const ok = selected === puzzle.solutionId
    setRevealed(true)
    setSolved((n) => n + (ok ? 1 : 0))
    awardBp(ok ? 40 : 8)
  }

  const revealParty = () => setRevealed(true)

  const toggleMark = (id) =>
    setMarked((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]))

  const finishPartyRound = () => {
    setPlayers((ps) => ps.map((p) => (marked.includes(p.id) ? { ...p, score: p.score + 1 } : p)))
    if (round >= rounds) {
      setPartyOver(true)
      return
    }
    setRound((r) => r + 1)
    setSeed(newSeed())
    setMarked([])
    setRevealed(false)
    setSeconds(60)
  }

  const addPlayer = () =>
    setPlayers((ps) =>
      ps.length >= 6 ? ps : [...ps, { id: `p${ps.length}`, name: `Jugador ${ps.length + 1}`, score: 0 }],
    )
  const removePlayer = () =>
    setPlayers((ps) => (ps.length <= 2 ? ps : ps.slice(0, -1)))
  const renamePlayer = (id, name) =>
    setPlayers((ps) => ps.map((p) => (p.id === id ? { ...p, name } : p)))

  // ---- MENU --------------------------------------------------------------
  if (!mode) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
          >
            <ArrowLeft size={16} /> Volver
          </button>
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
            El Detective
          </span>
        </div>

        <div className="p-4 rounded-2xl bg-gradient-to-br from-[#101828] to-[#0b1220] border border-slate-800">
          <div className="text-2xl">🕵️</div>
          <h2 className="text-lg font-black text-white mt-1">Deduce al culpable</h2>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            Cada caso tiene una única solución. Un número exacto de declaraciones es falso:
            encaja los datos y descarta sospechosos. Nunca hay que adivinar.
          </p>
        </div>

        <div>
          <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">
            Categoría
          </div>
          <CategoryPicker value={categoryId} onChange={setCategory} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={startSolo}
            className="p-4 rounded-2xl bg-cyan-500 text-slate-950 font-black text-sm shadow-lg shadow-cyan-500/25 active:scale-[0.99] cursor-pointer flex flex-col items-center gap-1"
          >
            <Play size={18} /> Caso único
            <span className="text-[10px] font-semibold opacity-80">Tú contra el enigma</span>
          </button>
          <button
            type="button"
            onClick={startParty}
            className="p-4 rounded-2xl bg-[#131b29] border border-amber-500/40 text-amber-200 font-black text-sm active:scale-[0.99] cursor-pointer flex flex-col items-center gap-1"
          >
            <Users size={18} /> Noche de Copas
            <span className="text-[10px] font-semibold opacity-80">Contrarreloj + amigos</span>
          </button>
        </div>
      </div>
    )
  }

  // ---- PARTY SETUP -------------------------------------------------------
  if (mode === 'copas' && !partyOver && round === 1 && !revealed && players.every((p) => p.score === 0)) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setMode(null)}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
          >
            <ArrowLeft size={16} /> Volver
          </button>
          <span className="text-[11px] font-bold text-amber-300 uppercase tracking-wider">
            🍸 Noche de Copas
          </span>
        </div>

        <div>
          <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">
            Categoría
          </div>
          <CategoryPicker value={categoryId} onChange={setCategory} />
        </div>

        <div>
          <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">
            Jugadores ({players.length})
          </div>
          <div className="space-y-2">
            {players.map((p) => (
              <input
                key={p.id}
                value={p.name}
                onChange={(e) => renamePlayer(p.id, e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-[#0e131f] border border-slate-800 text-sm text-white outline-none focus:border-amber-500/50"
              />
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={addPlayer}
              disabled={players.length >= 6}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs font-bold text-slate-300 disabled:opacity-40 cursor-pointer"
            >
              <Plus size={13} /> Añadir
            </button>
            <button
              type="button"
              onClick={removePlayer}
              disabled={players.length <= 2}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs font-bold text-slate-300 disabled:opacity-40 cursor-pointer"
            >
              <Minus size={13} /> Quitar
            </button>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">
            Rondas: {rounds}
          </div>
          <input
            type="range"
            min={3}
            max={10}
            value={rounds}
            onChange={(e) => setRounds(Number(e.target.value))}
            className="w-full accent-amber-500"
          />
        </div>

        <button
          type="button"
          onClick={startParty}
          className="w-full py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm shadow-lg shadow-amber-500/25 active:scale-[0.99] cursor-pointer"
        >
          Empezar la noche 🍸
        </button>
      </div>
    )
  }

  // ---- PARTY OVER --------------------------------------------------------
  if (mode === 'copas' && partyOver) {
    const ranked = [...players].sort((a, b) => b.score - a.score)
    return (
      <div className="space-y-5 text-center">
        <Trophy size={40} className="mx-auto text-amber-400" />
        <div>
          <h2 className="text-xl font-black text-white">{ranked[0].name} gana 🍸</h2>
          <p className="text-xs text-slate-400 mt-1">Noche de Copas terminada</p>
        </div>
        <div className="space-y-2">
          {ranked.map((p, i) => (
            <div
              key={p.id}
              className="flex items-center justify-between p-3 rounded-xl bg-[#0e131f] border border-slate-800"
            >
              <span className="text-sm font-bold text-white">
                #{i + 1} {p.name}
              </span>
              <span className="text-sm font-black text-amber-300">{p.score} pts</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={startParty}
            className="flex-1 py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm cursor-pointer"
          >
            Otra noche
          </button>
          <button
            type="button"
            onClick={() => setMode(null)}
            className="flex-1 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white font-black text-sm cursor-pointer"
          >
            Menú
          </button>
        </div>
      </div>
    )
  }

  // ---- SOLO / PARTY ROUND (case board) -----------------------------------
  const isParty = mode === 'copas'
  return (
    <div className="space-y-4">
      {isParty && (
        <div className="flex items-center justify-between text-xs">
          <span className="font-bold text-amber-300">
            Ronda {round}/{rounds}
          </span>
          <span className="text-slate-400">
            {players.map((p) => `${p.name}: ${p.score}`).join(' · ')}
          </span>
        </div>
      )}

      <CaseBoard
        puzzle={puzzle}
        selected={selected}
        onSelect={isParty ? undefined : setSelected}
        revealed={revealed}
        onReveal={isParty ? revealParty : !revealed ? confirmSolo : undefined}
        onNext={isParty ? finishPartyRound : nextSoloCase}
        onExit={() => setMode(null)}
        seconds={isParty ? seconds : null}
        party={isParty}
      />

      {isParty && revealed && (
        <div className="p-4 rounded-2xl bg-[#0e131f] border border-slate-800 space-y-3">
          <div className="text-[11px] font-black uppercase tracking-wider text-slate-400">
            ¿Quién acertó?
          </div>
          <div className="flex flex-wrap gap-2">
            {players.map((p) => {
              const on = marked.includes(p.id)
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleMark(p.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border cursor-pointer ${
                    on
                      ? 'bg-emerald-950/70 border-emerald-500/50 text-emerald-300'
                      : 'bg-slate-900 border-slate-800 text-slate-300'
                  }`}
                >
                  {on ? '✓ ' : ''}
                  {p.name}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {!isParty && revealed && solved >= 3 && (
        <div className="text-center text-[11px] text-slate-500">
          Casos resueltos en esta sesión: {solved}
        </div>
      )}
    </div>
  )
}
